import React, { useContext, useState, useEffect } from 'react'
import {
    Box,
    Container,
    Spinner,
    VStack,
    Text,
    Icon,
    Heading,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Badge,
    HStack,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    Checkbox,
    CheckboxGroup,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    IconButton,
    Tooltip,
    useDisclosure,
    Grid
} from '@chakra-ui/react'
import Navbar from '../compnents/Navbar'
import SideBar from '../compnents/SideBar'
import Footer from '../compnents/Footer'
import AuthContext from '../context/AuthContext';
import ThemeContext from '../context/ThemeContext';
import axios from 'axios';
import UploadYourFile from './UploadYourFile'
import Loading from '../compnents/Loading'
import FeatureUnlockContext from '../context/FeatureUnlockContext'
import { FaShieldAlt, FaKey, FaCopy, FaEdit, FaTrash } from 'react-icons/fa'
import { FiPlus } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'

const isProduction = process.env.NODE_ENV === "production";
const API_BASE_URL = isProduction ? process.env.REACT_APP_API_BASE_URL_PROD : process.env.REACT_APP_API_BASE_URL_DEV;

// Available scopes for API keys
const AVAILABLE_SCOPES = [
    { value: 'read:customers', label: 'Read Customers', description: 'Can read customer data' },
    { value: 'write:customers', label: 'Write Customers', description: 'Can create/update customer data' },
    { value: 'read:scores', label: 'Read Scores', description: 'Can read risk/bias scores' },
    { value: 'write:scores', label: 'Write Scores', description: 'Can write scores' },
    { value: 'read:dashboards', label: 'Read Dashboards', description: 'Can access analytics/dashboard endpoints' },
    { value: 'admin', label: 'Admin', description: 'Full access (like firm admin)' },
    { value: 'read:*', label: 'Read All', description: 'All read permissions' },
    { value: 'write:*', label: 'Write All', description: 'All write permissions' },
];

function ApiKey() {
    const { token, user, setUser, logout } = useContext(AuthContext);
    const { themeData } = useContext(ThemeContext);
    const { resetUnlocks } = useContext(FeatureUnlockContext);
    const [loading, setLoading] = useState(true);
    const [apiKeys, setApiKeys] = useState([]);
    const [loadingKeys, setLoadingKeys] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();

    // Modal states
    const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
    const { isOpen: isViewKeyOpen, onOpen: onViewKeyOpen, onClose: onViewKeyClose } = useDisclosure();

    // Form states
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        scopes: [],
        rate_limit_per_minute: 60,
        rate_limit_per_hour: 1000,
        rate_limit_per_day: 10000,
        expires_at: '',
    });

    const [editingKey, setEditingKey] = useState(null);
    const [createdKey, setCreatedKey] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#2B6CB0';
    const secondaryColor = themeData?.colors?.secondary || '#2C5282';
    const accentColor = themeData?.colors?.accent || '#63B3ED';
    const backgroundColor = themeData?.colors?.background || '#F7FAFC';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Text colors - fixed for dark theme
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.600';
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const headingTextColor = isDarkTheme ? '#F7FAFC' : 'gray.700';
    const strongTextColor = isDarkTheme ? '#F7FAFC' : 'gray.800';
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';
    const infoBg = isDarkTheme ? 'rgba(66, 153, 225, 0.1)' : 'gray.50';
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;

    // Fetch user data
    useEffect(() => {
        const fetchUser = async () => {
            if (!token || user) {
                setLoading(false);
                return;
            }

            try {
                const res = await axios.get(`${API_BASE_URL}/auth/check-user`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                console.log("checking user: ", res);

                if (res.status === 420) {
                    console.error('Invalid token');
                    resetUnlocks();
                    logout();
                    return;
                }

                setUser(res.data);
            } catch (error) {
                console.error('Error fetching user:', error);
                if (error.response?.status === 420) {
                    console.error('Token expired or invalid, logging out...');
                    resetUnlocks();
                    logout();
                }
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, [token, user, setUser, logout, resetUnlocks]);

    // Fetch API keys
    const fetchApiKeys = async () => {
        setLoadingKeys(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/content/api-keys`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { include_inactive: true }
            });
            setApiKeys(res.data.api_keys || []);
        } catch (error) {
            console.error('Error fetching API keys:', error);
            toast({
                title: 'Failed to fetch API keys',
                description: error.response?.data?.message || 'Please try again.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setLoadingKeys(false);
        }
    };

    useEffect(() => {
        if (user && user.role === 'admin' && user.subscription === 'business') {
            fetchApiKeys();
        }
        // eslint-disable-next-line
    }, [user]);

    // Create API key
    const handleCreateKey = async () => {
        if (!formData.name.trim()) {
            toast({
                title: 'Name is required',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                name: formData.name,
                description: formData.description || undefined,
                scopes: formData.scopes.length > 0 ? formData.scopes : undefined,
                rate_limit_per_minute: formData.rate_limit_per_minute,
                rate_limit_per_hour: formData.rate_limit_per_hour,
                rate_limit_per_day: formData.rate_limit_per_day,
                expires_at: formData.expires_at || undefined,
            };

            const res = await axios.post(`${API_BASE_URL}/content/api-keys`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setCreatedKey(res.data);
            onCreateClose();
            onViewKeyOpen();
            await fetchApiKeys();

            toast({
                title: 'API Key created successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            // Reset form
            setFormData({
                name: '',
                description: '',
                scopes: [],
                rate_limit_per_minute: 60,
                rate_limit_per_hour: 1000,
                rate_limit_per_day: 10000,
                expires_at: '',
            });
        } catch (error) {
            console.error('Error creating API key:', error);
            toast({
                title: 'Failed to create API key',
                description: error.response?.data?.message || 'Please try again.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Update API key
    const handleUpdateKey = async () => {
        if (!editingKey) return;

        setIsSubmitting(true);
        try {
            const payload = {
                name: formData.name || undefined,
                description: formData.description || undefined,
                scopes: formData.scopes.length > 0 ? formData.scopes : undefined,
                rate_limit_per_minute: formData.rate_limit_per_minute,
                rate_limit_per_hour: formData.rate_limit_per_hour,
                rate_limit_per_day: formData.rate_limit_per_day,
                expires_at: formData.expires_at || undefined,
                is_active: formData.is_active,
            };

            await axios.patch(`${API_BASE_URL}/content/api-keys/${editingKey.id}`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            onEditClose();
            await fetchApiKeys();

            toast({
                title: 'API Key updated successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            setEditingKey(null);
        } catch (error) {
            console.error('Error updating API key:', error);
            toast({
                title: 'Failed to update API key',
                description: error.response?.data?.message || 'Please try again.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Revoke API key
    const handleRevokeKey = async (keyId, keyName) => {
        if (!window.confirm(`Are you sure you want to revoke the API key "${keyName}"? This action cannot be undone.`)) {
            return;
        }

        try {
            await axios.post(
                `${API_BASE_URL}/content/api-keys/${keyId}/revoke`,
                { reason: 'Revoked by admin' },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            await fetchApiKeys();

            toast({
                title: 'API Key revoked successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Error revoking API key:', error);
            toast({
                title: 'Failed to revoke API key',
                description: error.response?.data?.message || 'Please try again.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        }
    };

    // Open edit modal
    const openEditModal = (key) => {
        setEditingKey(key);
        setFormData({
            name: key.name,
            description: key.description || '',
            scopes: key.scopes || [],
            rate_limit_per_minute: key.rate_limit_per_minute,
            rate_limit_per_hour: key.rate_limit_per_hour,
            rate_limit_per_day: key.rate_limit_per_day,
            expires_at: key.expires_at ? key.expires_at.split('T')[0] : '',
            is_active: key.is_active,
        });
        onEditOpen();
    };

    // Copy to clipboard
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast({
            title: 'Copied to clipboard',
            status: 'success',
            duration: 2000,
            isClosable: true,
        });
    };

    // Mask API key
    const maskApiKey = (keyPrefix) => {
        return `${keyPrefix}${'*'.repeat(40)}`;
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleString();
    };

    if (!token || (!user && loading)) {
        return (
            <Loading />
        );
    }

    if (!user) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" justifyContent="center" alignItems="center" bg={backgroundColor}>
                <Spinner size="xl" color={primaryColor} />
            </Box>
        );
    }

    if (user && user.noOfFileUploads === 0) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" bg={backgroundColor}>
                <Navbar />

                {(user.role === 'admin') && (
                    <UploadYourFile setUser={setUser} token={token} />
                )}

                {(user.role !== 'admin') && (
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        flex="1"
                        p={8}
                    >
                        <VStack spacing={4} textAlign="center">
                            <Text fontSize="xl" fontWeight="semibold" color={headingTextColor}>
                                No Dataset Available
                            </Text>
                            <Text fontSize="md" color={bodyTextColor} maxW="md">
                                Seems like your firm admin did not upload any datasets. Please contact admin and request to upload customer dataset.
                            </Text>
                        </VStack>
                    </Box>
                )}
            </Box>
        )
    }

    // Check if user has access
    const hasAccess = user.subscription === 'business' &&
        (user.role === 'admin');

    if (!hasAccess) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column">
                <Navbar />
                <Box display="flex" flex="1" bg={backgroundColor}>
                    <SideBar />
                    <Container maxW="container.xl" flex="1" py={8}>
                        <VStack spacing={6} align="center" justify="center" minH="60vh">
                            <Icon as={FaShieldAlt} boxSize={16} color={mutedTextColor} />
                            <Heading size="lg" color={headingTextColor}>
                                Api Keys
                            </Heading>
                            <Text color={bodyTextColor} textAlign="center">
                                This feature is only available for Business subscription users with higher access than yours.
                            </Text>
                        </VStack>
                    </Container>
                </Box>
                <Footer />
            </Box>
        );
    }

    return (
        <Box minH="100vh" display="flex" flexDirection="column">
            <Navbar />
            <Box display="flex" flex="1" bg={backgroundColor}>
                <SideBar />
                <Container maxW="container.xl" flex="1" py={8} px={{ base: 4, md: 6 }}>
                    <VStack spacing={6} align="stretch">
                        {/* Header */}
                        <HStack justify="space-between" align="center">
                            <Box>
                                <Heading as="h1" size="lg" color={headingTextColor} fontWeight="semibold">
                                    API Keys
                                </Heading>
                                <Text fontSize="sm" color={bodyTextColor} mt={1}>
                                    Manage API keys for programmatic access to your data
                                </Text>
                            </Box>
                            <HStack spacing={3}>
                                <Button
                                    variant="outline"
                                    borderColor={primaryColor}
                                    color={primaryColor}
                                    _hover={{
                                        bg: isDarkTheme ? 'rgba(66, 153, 225, 0.1)' : 'rgba(43, 108, 176, 0.1)',
                                        borderColor: accentColor,
                                        color: accentColor
                                    }}
                                    onClick={() => navigate('/docs')}
                                >
                                    View Docs
                                </Button>
                                <Button
                                    leftIcon={<FiPlus />}
                                    bg={primaryColor}
                                    color="white"
                                    _hover={{ bg: buttonHoverBg }}
                                    onClick={onCreateOpen}
                                >
                                    Create API Key
                                </Button>
                            </HStack>
                        </HStack>

                        {/* API Keys Table */}
                        <Box
                            bg={cardBg}
                            borderRadius="xl"
                            boxShadow="md"
                            border="1px"
                            borderColor={borderColor}
                            overflow="hidden"
                        >
                            {loadingKeys ? (
                                <Box textAlign="center" py={10}>
                                    <Spinner size="xl" color={isDarkTheme ? accentColor : primaryColor} />
                                    <Text mt={4} color={bodyTextColor}>Loading API keys...</Text>
                                </Box>
                            ) : apiKeys.length === 0 ? (
                                <VStack spacing={4} py={10}>
                                    <Icon as={FaKey} boxSize={12} color={mutedTextColor} />
                                    <Text color={bodyTextColor} fontSize="lg">No API keys created yet</Text>
                                    <Text color={mutedTextColor} fontSize="sm">
                                        Create your first API key to get started
                                    </Text>
                                </VStack>
                            ) : (
                                <Box overflowX="auto">
                                    <Table variant="simple">
                                        <Thead bg={infoBg}>
                                            <Tr>
                                                <Th color={bodyTextColor} borderColor={borderColor}>Name</Th>
                                                <Th color={bodyTextColor} borderColor={borderColor}>Key</Th>
                                                <Th color={bodyTextColor} borderColor={borderColor}>Scopes</Th>
                                                <Th color={bodyTextColor} borderColor={borderColor}>Rate Limits</Th>
                                                <Th color={bodyTextColor} borderColor={borderColor}>Usage</Th>
                                                <Th color={bodyTextColor} borderColor={borderColor}>Status</Th>
                                                <Th color={bodyTextColor} borderColor={borderColor}>Last Used</Th>
                                                <Th color={bodyTextColor} borderColor={borderColor}>Actions</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {apiKeys.map((key) => (
                                                <Tr key={key.id}>
                                                    <Td borderColor={borderColor}>
                                                        <VStack align="start" spacing={1}>
                                                            <Text color={strongTextColor} fontWeight="semibold" fontSize="sm">
                                                                {key.name}
                                                            </Text>
                                                            {key.description && (
                                                                <Text color={mutedTextColor} fontSize="xs" noOfLines={1}>
                                                                    {key.description}
                                                                </Text>
                                                            )}
                                                        </VStack>
                                                    </Td>
                                                    <Td borderColor={borderColor}>
                                                        <HStack>
                                                            <Text
                                                                color={bodyTextColor}
                                                                fontSize="xs"
                                                                fontFamily="mono"
                                                            >
                                                                {maskApiKey(key.key_prefix)}
                                                            </Text>
                                                        </HStack>
                                                    </Td>
                                                    <Td borderColor={borderColor}>
                                                        <HStack spacing={1} flexWrap="wrap">
                                                            {key.scopes?.map((scope) => (
                                                                <Badge key={scope} colorScheme="blue" fontSize="xs">
                                                                    {scope}
                                                                </Badge>
                                                            ))}
                                                        </HStack>
                                                    </Td>
                                                    <Td borderColor={borderColor}>
                                                        <VStack align="start" spacing={0}>
                                                            <Text color={bodyTextColor} fontSize="xs">
                                                                {key.rate_limit_per_minute}/min
                                                            </Text>
                                                            <Text color={bodyTextColor} fontSize="xs">
                                                                {key.rate_limit_per_hour}/hr
                                                            </Text>
                                                            <Text color={bodyTextColor} fontSize="xs">
                                                                {key.rate_limit_per_day}/day
                                                            </Text>
                                                        </VStack>
                                                    </Td>
                                                    <Td borderColor={borderColor}>
                                                        <Text color={bodyTextColor} fontSize="sm">
                                                            {key.usage_count || 0}
                                                        </Text>
                                                    </Td>
                                                    <Td borderColor={borderColor}>
                                                        <Badge
                                                            colorScheme={key.is_active ? 'green' : 'red'}
                                                            fontSize="xs"
                                                        >
                                                            {key.is_active ? 'Active' : 'Inactive'}
                                                        </Badge>
                                                    </Td>
                                                    <Td borderColor={borderColor}>
                                                        <Text color={bodyTextColor} fontSize="xs">
                                                            {formatDate(key.last_used_at)}
                                                        </Text>
                                                    </Td>
                                                    <Td borderColor={borderColor}>
                                                        <HStack spacing={2}>
                                                            <Tooltip label="Edit">
                                                                <IconButton
                                                                    icon={<FaEdit />}
                                                                    size="sm"
                                                                    colorScheme="blue"
                                                                    variant="ghost"
                                                                    onClick={() => openEditModal(key)}
                                                                    isDisabled={!key.is_active}
                                                                />
                                                            </Tooltip>
                                                            <Tooltip label="Revoke">
                                                                <IconButton
                                                                    icon={<FaTrash />}
                                                                    size="sm"
                                                                    colorScheme="red"
                                                                    variant="ghost"
                                                                    onClick={() => handleRevokeKey(key.id, key.name)}
                                                                    isDisabled={!key.is_active}
                                                                />
                                                            </Tooltip>
                                                        </HStack>
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </Box>
                            )}
                        </Box>
                    </VStack>
                </Container>
            </Box>
            <Footer />

            {/* Create API Key Modal */}
            <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="xl">
                <ModalOverlay />
                <ModalContent bg={cardBg}>
                    <ModalHeader color={headingTextColor}>Create New API Key</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <FormControl isRequired>
                                <FormLabel color={bodyTextColor}>Name</FormLabel>
                                <Input
                                    placeholder="Production API Key"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    color={strongTextColor}
                                    borderColor={borderColor}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel color={bodyTextColor}>Description</FormLabel>
                                <Textarea
                                    placeholder="API key for production integration"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    color={strongTextColor}
                                    borderColor={borderColor}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel color={bodyTextColor}>Scopes (Permissions)</FormLabel>
                                <CheckboxGroup
                                    value={formData.scopes}
                                    onChange={(values) => setFormData({ ...formData, scopes: values })}
                                >
                                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                                        {AVAILABLE_SCOPES.map((scope) => (
                                            <Box key={scope.value}>
                                                <Checkbox value={scope.value} colorScheme="blue">
                                                    <Text color={bodyTextColor} fontSize="sm">{scope.label}</Text>
                                                </Checkbox>
                                                <Text color={mutedTextColor} fontSize="xs" ml={6}>
                                                    {scope.description}
                                                </Text>
                                            </Box>
                                        ))}
                                    </Grid>
                                </CheckboxGroup>
                            </FormControl>

                            {/* <FormControl>
                                <FormLabel color={bodyTextColor}>Rate Limit (per minute)</FormLabel>
                                <NumberInput
                                    value={formData.rate_limit_per_minute}
                                    onChange={(_, value) => setFormData({ ...formData, rate_limit_per_minute: value })}
                                    min={1}
                                    max={10000}
                                >
                                    <NumberInputField color={strongTextColor} borderColor={borderColor} />
                                    <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                    </NumberInputStepper>
                                </NumberInput>
                            </FormControl>

                            <FormControl>
                                <FormLabel color={bodyTextColor}>Rate Limit (per hour)</FormLabel>
                                <NumberInput
                                    value={formData.rate_limit_per_hour}
                                    onChange={(_, value) => setFormData({ ...formData, rate_limit_per_hour: value })}
                                    min={1}
                                    max={100000}
                                >
                                    <NumberInputField color={strongTextColor} borderColor={borderColor} />
                                    <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                    </NumberInputStepper>
                                </NumberInput>
                            </FormControl>

                            <FormControl>
                                <FormLabel color={bodyTextColor}>Rate Limit (per day)</FormLabel>
                                <NumberInput
                                    value={formData.rate_limit_per_day}
                                    onChange={(_, value) => setFormData({ ...formData, rate_limit_per_day: value })}
                                    min={1}
                                    max={1000000}
                                >
                                    <NumberInputField color={strongTextColor} borderColor={borderColor} />
                                    <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                    </NumberInputStepper>
                                </NumberInput>
                            </FormControl> */}

                            <FormControl>
                                <FormLabel color={bodyTextColor}>Expiration Date (Optional)</FormLabel>
                                <Input
                                    type="date"
                                    value={formData.expires_at}
                                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                                    color={strongTextColor}
                                    borderColor={borderColor}
                                />
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onCreateClose}>
                            Cancel
                        </Button>
                        <Button
                            bg={primaryColor}
                            color="white"
                            _hover={{ bg: buttonHoverBg }}
                            onClick={handleCreateKey}
                            isLoading={isSubmitting}
                        >
                            Create Key
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Edit API Key Modal */}
            <Modal isOpen={isEditOpen} onClose={onEditClose} size="xl">
                <ModalOverlay />
                <ModalContent bg={cardBg}>
                    <ModalHeader color={headingTextColor}>Edit API Key</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <FormControl>
                                <FormLabel color={bodyTextColor}>Name</FormLabel>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    color={strongTextColor}
                                    borderColor={borderColor}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel color={bodyTextColor}>Description</FormLabel>
                                <Textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    color={strongTextColor}
                                    borderColor={borderColor}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel color={bodyTextColor}>Scopes (Permissions)</FormLabel>
                                <CheckboxGroup
                                    value={formData.scopes}
                                    onChange={(values) => setFormData({ ...formData, scopes: values })}
                                >
                                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                                        {AVAILABLE_SCOPES.map((scope) => (
                                            <Box key={scope.value}>
                                                <Checkbox value={scope.value} colorScheme="blue">
                                                    <Text color={bodyTextColor} fontSize="sm">{scope.label}</Text>
                                                </Checkbox>
                                                <Text color={mutedTextColor} fontSize="xs" ml={6}>
                                                    {scope.description}
                                                </Text>
                                            </Box>
                                        ))}
                                    </Grid>
                                </CheckboxGroup>
                            </FormControl>

                            {/* <FormControl>
                                <FormLabel color={bodyTextColor}>Rate Limit (per minute)</FormLabel>
                                <NumberInput
                                    value={formData.rate_limit_per_minute}
                                    onChange={(_, value) => setFormData({ ...formData, rate_limit_per_minute: value })}
                                    min={1}
                                    max={10000}
                                >
                                    <NumberInputField color={strongTextColor} borderColor={borderColor} />
                                    <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                    </NumberInputStepper>
                                </NumberInput>
                            </FormControl>

                            <FormControl>
                                <FormLabel color={bodyTextColor}>Rate Limit (per hour)</FormLabel>
                                <NumberInput
                                    value={formData.rate_limit_per_hour}
                                    onChange={(_, value) => setFormData({ ...formData, rate_limit_per_hour: value })}
                                    min={1}
                                    max={100000}
                                >
                                    <NumberInputField color={strongTextColor} borderColor={borderColor} />
                                    <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                    </NumberInputStepper>
                                </NumberInput>
                            </FormControl>

                            <FormControl>
                                <FormLabel color={bodyTextColor}>Rate Limit (per day)</FormLabel>
                                <NumberInput
                                    value={formData.rate_limit_per_day}
                                    onChange={(_, value) => setFormData({ ...formData, rate_limit_per_day: value })}
                                    min={1}
                                    max={1000000}
                                >
                                    <NumberInputField color={strongTextColor} borderColor={borderColor} />
                                    <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                    </NumberInputStepper>
                                </NumberInput>
                            </FormControl> */}

                            <FormControl>
                                <FormLabel color={bodyTextColor}>Expiration Date</FormLabel>
                                <Input
                                    type="date"
                                    value={formData.expires_at}
                                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                                    color={strongTextColor}
                                    borderColor={borderColor}
                                />
                            </FormControl>

                            <FormControl display="flex" alignItems="center">
                                <FormLabel color={bodyTextColor} mb="0">Active</FormLabel>
                                <Checkbox
                                    isChecked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    colorScheme="blue"
                                />
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onEditClose}>
                            Cancel
                        </Button>
                        <Button
                            bg={primaryColor}
                            color="white"
                            _hover={{ bg: buttonHoverBg }}
                            onClick={handleUpdateKey}
                            isLoading={isSubmitting}
                        >
                            Update Key
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* View Created Key Modal */}
            <Modal isOpen={isViewKeyOpen} onClose={onViewKeyClose} size="lg" closeOnOverlayClick={false}>
                <ModalOverlay />
                <ModalContent bg={cardBg}>
                    <ModalHeader color={headingTextColor}>API Key Created Successfully</ModalHeader>
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <Alert status="warning" borderRadius="md">
                                <AlertIcon />
                                <Box>
                                    <AlertTitle fontSize="sm">Important!</AlertTitle>
                                    <AlertDescription fontSize="xs">
                                        Store this API key securely. It will not be shown again.
                                    </AlertDescription>
                                </Box>
                            </Alert>

                            {createdKey && (
                                <>
                                    <Box
                                        bg={infoBg}
                                        p={4}
                                        borderRadius="md"
                                        border="1px"
                                        borderColor={borderColor}
                                    >
                                        <Text fontSize="xs" color={mutedTextColor} mb={2}>
                                            API Key
                                        </Text>
                                        <HStack>
                                            <Text
                                                fontSize="sm"
                                                fontFamily="mono"
                                                color={strongTextColor}
                                                wordBreak="break-all"
                                                flex="1"
                                            >
                                                {createdKey.plain_key}
                                            </Text>
                                            <IconButton
                                                icon={<FaCopy />}
                                                size="sm"
                                                colorScheme="blue"
                                                onClick={() => copyToClipboard(createdKey.plain_key)}
                                            />
                                        </HStack>
                                    </Box>

                                    <Box
                                        bg={infoBg}
                                        p={4}
                                        borderRadius="md"
                                        border="1px"
                                        borderColor={borderColor}
                                    >
                                        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                            <Box>
                                                <Text fontSize="xs" color={mutedTextColor}>Name</Text>
                                                <Text fontSize="sm" color={strongTextColor}>
                                                    {createdKey.api_key.name}
                                                </Text>
                                            </Box>
                                            <Box>
                                                <Text fontSize="xs" color={mutedTextColor}>Status</Text>
                                                <Badge colorScheme="green">Active</Badge>
                                            </Box>
                                        </Grid>
                                    </Box>
                                </>
                            )}
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            bg={primaryColor}
                            color="white"
                            _hover={{ bg: buttonHoverBg }}
                            onClick={() => {
                                onViewKeyClose();
                                setCreatedKey(null);
                            }}
                        >
                            I've Saved the Key
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    )
}

export default ApiKey